/**
 * CalendarEventModal
 *
 * Main container for event create/edit modal.
 * Google Calendar-style bottom sheet with modular sections.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Modal,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Animated, {
  FadeIn,
  FadeOut,
  SlideInDown,
  SlideOutDown,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { BorderRadius, Spacing } from '@/constants/spacing';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useEventFormState, type EventFormMode } from '@/hooks/use-event-form-state';
import { useKeyboardHeight } from '@/hooks/use-keyboard-height';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { deviceCalendarService } from '@/lib/calendar/DeviceCalendarService';
import { useSupabaseAuth } from '@/lib/supabase';
import { EventDescriptionSectionParser } from '@/lib/utils/EventDescriptionSectionParser';
import { EventDescriptionParser } from '@/lib/utils/EventDescriptionParser';
import { TaskCountResolver } from '@/lib/utils/TaskCountResolver';
import { TaskIndicator } from '@/components/shared/TaskIndicator';
import { EventParticipantsManager } from '@/lib/managers/EventParticipantsManager';
import { usePillInvitePrompt } from '@/hooks/use-pill-invite-prompt';
import { CalendarEventFormatter } from '@/lib/calendar/CalendarEventFormatter';
import type { CalendarEvent, CreateCalendarEventInput, UpdateCalendarEventInput } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';

import { CalendarSelectorModal } from '@/components/calendar/CalendarSelectorModal';
import { RecurrencePicker } from '@/components/calendar/RecurrencePicker';
import { EventCalendarRow } from './EventCalendarRow';
import { EventDateTimeRow } from './EventDateTimeRow';
import { EventDeleteRow } from './EventDeleteRow';
import { EventDescriptionSection } from './EventDescriptionSection';
import { EventGuestsSection } from './EventGuestsSection';
import { EventCreatedByRow } from './EventCreatedByRow';
import { EventFamilyLabelsSection } from './EventFamilyLabelsSection';
import { EventLocationRow } from './EventLocationRow';
import { EventModalFooter } from './EventModalFooter';
import { EventNotificationsRow } from './EventNotificationsRow';
import { EventRecurrenceRow } from './EventRecurrenceRow';
import { EventTitleSection } from './EventTitleSection';
import { EventTransportSection } from './EventTransportSection';
import { EventWeatherRow } from './EventWeatherRow';
import { DateTimePickerModal } from './date-time-picker';
import type { DateTimePickerTarget, DateTimePickerResult } from '@/types/date-time-picker';

export type CalendarEventModalProps = {
  visible: boolean;
  mode: EventFormMode;
  initialEvent?: CalendarEvent | null;
  preselectedDate?: string | null;
  preselectedTime?: string | null;
  defaultTitle?: string;
  defaultDescription?: string;
  writableCalendars: DeviceCalendar[];
  allCalendars?: DeviceCalendar[];
  defaultCalendarId?: string | null;
  familyMembers?: FamilyMember[];
  calendarMappings?: CalendarMemberMapping[];
  onSave: (
    input: (CreateCalendarEventInput | UpdateCalendarEventInput) & {
      targetCalendarId?: string;
    },
  ) => Promise<void>;
  onDelete?: () => void;
  onDismiss: () => void;
  onInstantSave?: () => void;
  isSubmitting?: boolean;
};

export function CalendarEventModal({
  visible,
  mode,
  initialEvent,
  preselectedDate,
  preselectedTime,
  defaultTitle,
  defaultDescription,
  writableCalendars,
  allCalendars,
  defaultCalendarId,
  familyMembers = [],
  calendarMappings = [],
  onSave,
  onDelete,
  onDismiss,
  onInstantSave,
  isSubmitting = false,
}: CalendarEventModalProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'] as typeof Colors.light;
  const { session } = useSupabaseAuth();
  const { top: topInset } = useSafeAreaInsets();
  const { height: screenHeight } = useWindowDimensions();
  const { keyboardHeight, isKeyboardVisible } = useKeyboardHeight();

  const COLLAPSED_HEIGHT = screenHeight * 0.8;
  const EXPANDED_HEIGHT = screenHeight - topInset - 8; // 8px gap from top
  const FOOTER_HEIGHT = 60;

  // Form state hook
  const form = useEventFormState({
    mode,
    initialEvent,
    preselectedDate,
    preselectedTime,
    defaultTitle,
    defaultDescription,
    writableCalendars,
    defaultCalendarId,
    familyMembers,
    calendarMappings,
    session,
  });

  // Pill invite prompt — prompts to send calendar invite when tapping a family member pill
  // Uses allCalendars (includes read-only) so we can resolve emails for members linked to non-writable calendars
  const { promptForInvite } = usePillInvitePrompt({
    calendarMappings,
    calendars: allCalendars ?? writableCalendars,
    onConfirmInvite: form.handlers.handleAddPillLinkedInvite,
  });

  const handlePillToggle = useCallback((familyMember: FamilyMember) => {
    console.log('[handlePillToggle] tapped:', familyMember.name, familyMember.id);
    const isCurrentlySelected = form.state.participants.some(
      p => p.role === 'label' && p.familyMemberId === familyMember.id,
    );
    console.log('[handlePillToggle] isCurrentlySelected:', isCurrentlySelected);
    form.handlers.handleToggleForMember(familyMember);
    // Prompt for invite only on select (not deselect)
    if (!isCurrentlySelected) {
      console.log('[handlePillToggle] calling promptForInvite');
      promptForInvite(familyMember);
    }
  }, [form.state.participants, form.handlers, promptForInvite]);

  // Animated sheet height
  const sheetHeight = useSharedValue(COLLAPSED_HEIGHT);

  useEffect(() => {
    if (isKeyboardVisible) {
      sheetHeight.value = withTiming(EXPANDED_HEIGHT, { duration: 250 });
    } else {
      sheetHeight.value = withTiming(COLLAPSED_HEIGHT, { duration: 200 });
    }
  }, [isKeyboardVisible, EXPANDED_HEIGHT, COLLAPSED_HEIGHT, sheetHeight]);

  const sheetAnimatedStyle = useAnimatedStyle(() => ({
    height: sheetHeight.value,
  }));

  // Picker state
  const [pickerTarget, setPickerTarget] = useState<DateTimePickerTarget | null>(null);
  const [showRecurrencePicker, setShowRecurrencePicker] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  // Ref to always have latest buildSaveInput (avoids stale closure issues)
  const buildSaveInputRef = useRef(form.handlers.buildSaveInput);
  useEffect(() => {
    buildSaveInputRef.current = form.handlers.buildSaveInput;
  });

  // Initialize form when modal opens (only on visibility change, not on every render)
  useEffect(() => {
    if (visible) {
      form.handlers.initializeForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Get current date/time values for the combined picker based on target
  const getPickerValues = useCallback((): { date: string; time: string | null } => {
    if (!pickerTarget) return { date: form.state.startDate, time: form.state.startTime };

    switch (pickerTarget.kind) {
      case 'start':
        return { date: form.state.startDate, time: form.state.startTime };
      case 'end':
        return { date: form.state.endDate, time: form.state.endTime };
      case 'reminder': {
        const reminder = form.state.reminders.find(r => r.id === pickerTarget.id);
        return {
          date: reminder?.date ?? form.state.startDate,
          time: reminder?.time ?? form.state.startTime,
        };
      }
    }
  }, [pickerTarget, form.state]);

  // Handle combined date/time picker confirm
  const handleDateTimeConfirm = useCallback((result: DateTimePickerResult) => {
    if (!pickerTarget) return;

    switch (pickerTarget.kind) {
      case 'start':
        if (result.date) form.setters.setStartDate(result.date);
        if (result.time) form.setters.setStartTime(result.time);
        break;
      case 'end':
        if (result.date) form.setters.setEndDate(result.date);
        if (result.time) form.setters.setEndTime(result.time);
        break;
      case 'reminder': {
        const reminder = form.state.reminders.find(r => r.id === pickerTarget.id);
        if (reminder) {
          form.handlers.handleUpdateReminder(pickerTarget.id, {
            date: result.date ?? reminder.date,
            time: result.time ?? reminder.time,
          });
        }
        break;
      }
    }

    setPickerTarget(null);
  }, [pickerTarget, form]);

  // Handle save (uses ref to always get latest buildSaveInput)
  const handleSave = useCallback(async () => {
    try {
      const input = buildSaveInputRef.current();
      await onSave(input);
    } catch (error) {
      console.error('[CalendarEventModal] Save error:', error);
    }
  }, [onSave]);

  // Handle backdrop press with unsaved changes confirmation
  const handleBackdropPress = useCallback(() => {
    if (form.hasChanges) {
      Alert.alert(
        'Save or discard changes?',
        'You have unsaved changes. Would you like to save or discard them?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Discard', style: 'destructive', onPress: onDismiss },
          { text: 'Save', onPress: handleSave },
        ]
      );
    } else {
      onDismiss();
    }
  }, [form.hasChanges, onDismiss, handleSave]);

  // Instant save when a task checkbox is toggled (edit mode only)
  const handleTaskToggled = useCallback((serializedDescription: string) => {
    if (mode !== 'edit' || !initialEvent?.id) return;

    // Parse the field-level description to get tasks and text
    const parsed = EventDescriptionParser.parse(serializedDescription);
    const plainText = parsed.textLines.join('\n');

    // Get family member IDs from current participants
    const familyMemberIds = EventParticipantsManager.getFamilyMemberIds(form.state.participants);

    // Rebuild full description with all section markers
    const fullDescription = EventDescriptionSectionParser.serialize(
      plainText.trim(),
      parsed.checklistItems,
      familyMemberIds,
      form.state.transport,
      form.state.taskColor,
      form.state.meToggleOff,
    );

    // Update snapshot synchronously so hasChanges recomputes correctly
    // when the description state update propagates (ref update must happen
    // before the next render cycle where useMemo recalculates)
    form.handlers.updateDescriptionSnapshot(serializedDescription);

    // For recurring events, default to "this event only" for instant saves
    // This is the safest default for quick task toggles (no disruptive prompt)
    const recurringOptions = initialEvent.recurrence
      ? {
          futureEvents: false,
          instanceStartDate: CalendarEventFormatter.toDate(
            initialEvent.startDate,
            initialEvent.startTime,
          ),
        }
      : undefined;

    // Fire-and-forget update to device calendar, then notify parent to refresh
    deviceCalendarService
      .updateEvent(
        initialEvent.id,
        { description: fullDescription } as UpdateCalendarEventInput,
        recurringOptions,
      )
      .then(() => {
        onInstantSave?.();
      })
      .catch((error) => {
        console.error('[CalendarEventModal] Instant task save failed:', error);
      });
  }, [mode, initialEvent?.id, initialEvent?.recurrence, initialEvent?.startDate, initialEvent?.startTime, form.state.participants, form.state.transport, form.state.taskColor, form.state.meToggleOff, form.handlers, onInstantSave]);

  return (
    <>
      <Modal visible={visible} animationType="none" transparent onRequestClose={handleBackdropPress} statusBarTranslucent>
        <View style={styles.overlay}>
          {/* Backdrop */}
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(200)}
            style={styles.backdrop}
          >
            <Pressable style={StyleSheet.absoluteFill} onPress={handleBackdropPress} />
          </Animated.View>

          {/* Sheet */}
          <Animated.View
            entering={SlideInDown.duration(250)}
            exiting={SlideOutDown.duration(200)}
            style={[
              styles.sheet,
              { backgroundColor: palette.background },
              sheetAnimatedStyle,
            ]}
          >
            <SafeAreaView edges={['bottom']} style={styles.safeArea}>
              {/* Drag handle */}
              <View style={styles.handleRow}>
                <View style={styles.handleContainer}>
                  <View style={[styles.handle, { backgroundColor: palette.border }]} />
                </View>
              </View>

              {/* Content */}
              <KeyboardAwareScrollView
                style={styles.scrollView}
                contentContainerStyle={[styles.scrollContent, { paddingBottom: Spacing.xl + FOOTER_HEIGHT }]}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
                enableOnAndroid={true}
                extraScrollHeight={50}
                enableAutomaticScroll={true}
              >
                <View style={styles.titleRow}>
                  <View style={styles.titleSectionFlex}>
                    <EventTitleSection
                      value={form.state.title}
                      onChange={form.setters.setTitle}
                      palette={palette}
                    />
                  </View>
                  <View style={styles.topRightContainer}>
                    <TaskIndicator
                      taskCount={TaskCountResolver.resolve(form.state.description)}
                      size="large"
                    />
                  </View>
                </View>

                <View style={[styles.divider, { backgroundColor: palette.border }]} />

                <EventDateTimeRow
                  allDay={form.state.allDay}
                  startDate={form.state.startDate}
                  startTime={form.state.startTime}
                  endDate={form.state.endDate}
                  endTime={form.state.endTime}
                  onToggleAllDay={form.handlers.handleToggleAllDay}
                  onPressStartDate={() => setPickerTarget({ kind: 'start', mode: form.state.allDay ? 'date' : 'datetime' })}
                  onPressEndDate={() => setPickerTarget({ kind: 'end', mode: form.state.allDay ? 'date' : 'datetime' })}
                  palette={palette}
                />

                <EventWeatherRow
                  date={form.state.startDate}
                  palette={palette}
                />

                <EventDescriptionSection
                  value={form.state.description}
                  onChange={form.setters.setDescription}
                  palette={palette}
                  taskColor={form.state.taskColor}
                  onTaskColorChanged={form.setters.setTaskColor}
                  onTaskToggled={mode === 'edit' ? handleTaskToggled : undefined}
                />

                <EventFamilyLabelsSection
                  forLabels={form.state.participants.filter(p => p.role === 'label')}
                  familyMembers={familyMembers}
                  onToggleForMember={handlePillToggle}
                  isCalendarLinkedToMe={form.state.isCalendarLinkedToMe}
                  meToggleOff={form.state.meToggleOff}
                  onToggleMeForEvent={form.handlers.handleToggleMeForEvent}
                  palette={palette}
                />

                <EventGuestsSection
                  participants={form.state.participants}
                  onAddEmail={form.handlers.handleAddEmailInvitee}
                  onRemoveParticipant={form.handlers.handleRemoveParticipant}
                  familyMembers={familyMembers}
                  calendarMappings={calendarMappings}
                  calendars={allCalendars ?? writableCalendars}
                  palette={palette}
                />

                <EventNotificationsRow
                  reminders={form.state.reminders}
                  onAdd={form.handlers.handleAddReminder}
                  onPressDate={(id) => setPickerTarget({ kind: 'reminder', id, mode: 'datetime' })}
                  onPressTime={(id) => setPickerTarget({ kind: 'reminder', id, mode: 'datetime' })}
                  onRemove={form.handlers.handleRemoveReminder}
                  palette={palette}
                />

                <EventTransportSection
                  transport={form.state.transport}
                  onChange={form.setters.setTransport}
                  familyMembers={familyMembers}
                  palette={palette}
                />

                <View style={[styles.divider, { backgroundColor: palette.border }]} />

                <EventCreatedByRow
                  organizer={form.state.organizer}
                  palette={palette}
                />

                <EventCalendarRow
                  calendars={writableCalendars}
                  selectedId={form.state.selectedCalendarId}
                  onPress={() => setShowCalendarModal(true)}
                  palette={palette}
                  mode={mode}
                  calendarMappings={calendarMappings}
                  familyMembers={familyMembers}
                />

                <EventLocationRow
                  value={form.state.location}
                  onChange={form.setters.setLocation}
                  palette={palette}
                />

                <View style={[styles.divider, { backgroundColor: palette.border }]} />

                <EventRecurrenceRow
                  value={form.state.recurrence}
                  onPress={() => setShowRecurrencePicker(true)}
                  palette={palette}
                />

                {mode === 'edit' && onDelete && (
                  <EventDeleteRow
                    eventTitle={form.state.title || 'this event'}
                    onDelete={onDelete}
                    palette={palette}
                  />
                )}
              </KeyboardAwareScrollView>

              {/* Footer - stays visible above keyboard */}
              <View style={{ marginBottom: keyboardHeight }}>
                <EventModalFooter
                  onCancel={onDismiss}
                  onSave={handleSave}
                  isSubmitting={isSubmitting}
                  isValid={form.isValid}
                  hasChanges={form.hasChanges}
                  palette={palette}
                />
              </View>
            </SafeAreaView>
          </Animated.View>
        </View>
      </Modal>

      {/* Combined Date/Time Picker Modal */}
      <DateTimePickerModal
        visible={pickerTarget !== null}
        mode={pickerTarget?.mode ?? 'datetime'}
        selectedDate={getPickerValues().date}
        selectedTime={getPickerValues().time}
        onConfirm={handleDateTimeConfirm}
        onClose={() => setPickerTarget(null)}
        accentColor={palette.tint}
        title={
          pickerTarget
            ? pickerTarget.kind === 'start'
              ? 'Start'
              : pickerTarget.kind === 'end'
                ? 'End'
                : 'Notification'
            : ''
        }
      />

      {/* Recurrence Picker */}
      <RecurrencePicker
        visible={showRecurrencePicker}
        value={form.state.recurrence}
        onChange={form.setters.setRecurrence}
        onDismiss={() => setShowRecurrencePicker(false)}
        startDate={form.state.startDate}
      />

      {/* Calendar Selector */}
      <CalendarSelectorModal
        visible={showCalendarModal}
        calendars={writableCalendars}
        selectedCalendarId={form.state.selectedCalendarId}
        onSelect={form.handlers.handleCalendarChange}
        onClose={() => setShowCalendarModal(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  sheet: {
    borderTopLeftRadius: BorderRadius.lg,
    borderTopRightRadius: BorderRadius.lg,
    overflow: 'hidden',
  },
  safeArea: {
    flex: 1,
  },
  handleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  handleContainer: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: Spacing.md,
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: BorderRadius.xxs,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleSectionFlex: {
    flex: 1,
  },
  topRightContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingRight: Spacing.lg,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: Spacing.xl,
  },
  divider: {
    height: 1,
    marginHorizontal: Spacing.lg,
    marginVertical: Spacing.sm,
  },
});
